/**
 * JSON-LD Schema Generators for SEO & Rich Snippets
 */

/**
 * Generates LodgingBusiness / Hotel schema for property detail pages
 */
export function generateLodgingBusinessSchema(property, canonicalUrl) {
  if (!property) return null;

  const images = (property.photos || []).filter((p) => p && p.active !== false).map((p) => p.url).filter(Boolean);
  const priceVal =
    property.costPerPersonFrom ||
    property.priceOverride ||
    property.startingPrice ||
    property.priceFrom ||
    property.cost;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'LodgingBusiness',
    name: property.title,
    description: property.briefDescription || property.metaData?.description || '',
    image: images.length > 0 ? images : undefined,
    address: {
      '@type': 'PostalAddress',
      addressLocality:
        property.visibleOn?.destinationArea?.name ||
        property.visibleOn?.destination?.name ||
        'Maasai Mara',
      addressCountry: 'KE',
    },
  };

  if (canonicalUrl) {
    schema['@id'] = canonicalUrl;
    schema.url = canonicalUrl;
  }

  if (priceVal) {
    schema.priceRange = `$${priceVal} pppns`;
  }

  if (property.location?.coordinates && property.location.coordinates.length === 2) {
    schema.geo = {
      '@type': 'GeoCoordinates',
      longitude: property.location.coordinates[0],
      latitude: property.location.coordinates[1],
    };
  }

  return schema;
}

/**
 * Generates ItemList schema for accommodation listing page
 */
export function generateItemListSchema(properties = [], baseUrl = '') {
  if (!Array.isArray(properties) || properties.length === 0) return null;

  const itemListElement = properties.slice(0, 20).map((prop, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    url: `${baseUrl.replace(/\/$/, '')}/accommodation/${prop.slug || prop._id}`,
    name: prop.title,
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Featured Safari Lodges & Camps',
    itemListElement,
  };
}

/**
 * Generates BreadcrumbList schema
 */
export function generateBreadcrumbSchema(crumbs = [], baseUrl = '') {
  if (!Array.isArray(crumbs) || crumbs.length === 0) return null;

  const itemListElement = crumbs.map((crumb, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: crumb.title,
    item: crumb.url ? `${baseUrl.replace(/\/$/, '')}${crumb.url}` : undefined,
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement,
  };
}

/**
 * Generates TouristTrip schema for safari detail pages
 */
export function generateTouristTripSchema(safari, canonicalUrl) {
  if (!safari) return null;

  const images = (safari.photos || []).filter((p) => p && p.active !== false).map((p) => p.url).filter(Boolean);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'TouristTrip',
    name: safari.title,
    description: safari.briefDescription || safari.metaData?.description || '',
    image: images.length > 0 ? images : undefined,
    touristType: 'Safari Enthusiasts',
  };

  if (canonicalUrl) {
    schema['@id'] = canonicalUrl;
    schema.url = canonicalUrl;
  }

  return schema;
}

/**
 * Generates Organization schema for home page
 */
export function generateOrganizationSchema(baseUrl = 'https://www.maasaimara.com') {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Wilderness Bookings',
    url: baseUrl,
    logo: `${baseUrl.replace(/\/$/, '')}/logo.png`,
    sameAs: [
      'https://www.facebook.com/wildernessbookings',
      'https://www.instagram.com/wildernessbookings',
    ],
  };
}
